import { useCallback, useEffect, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Divider from '~/bootcamp/Activities/components/Divider'

import { generateDenominatorNumerator } from './FractionVisualize.utils'

const MIN_NUMERATOR = [1, 1, 1]
const MAX_NUMERATOR = [4, 5, 8]
const MIN_DENOMINATOR = [2, 2, 2]
const MAX_DENOMINATOR = [4, 5, 8]
const MAX_HISTORY_LENGTH = [3, 3, 3]

function FractionVisualize({
  answerState,
  actionButtonsFC,
  difficultyLevel = 2,
  onNextQuestionClick,
  onAnswerStateChange,
}: ActivityProps): React.ReactElement {
  const [numerator, setNumerator] = useState(MIN_NUMERATOR[0])
  const [denominator, setDenominator] = useState(MIN_DENOMINATOR[0])
  const [answer, setAnswer] = useState('')
  const [taskHistory, setTaskHistory] = useState<Array<{ numerator: number; denominator: number }>>([])
  const [clickedSquares, setClickedSquares] = useState(new Array(denominator).fill(false))

  const generateNumbers = useCallback((): { numerator: number; denominator: number } => {
    return generateDenominatorNumerator(
      MIN_NUMERATOR[difficultyLevel - 1],
      MAX_NUMERATOR[difficultyLevel - 1],
      MIN_DENOMINATOR[difficultyLevel - 1],
      MAX_DENOMINATOR[difficultyLevel - 1],
      taskHistory,
    )
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    const generatedNumbers = generateNumbers()
    setNumerator(generatedNumbers.numerator)
    setDenominator(generatedNumbers.denominator)
    setAnswer(generatedNumbers.numerator.toString())
  }, [generateNumbers])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const countClickedSquares = clickedSquares.filter(Boolean).length
    const isCorrect = countClickedSquares.toString() === answer
    setClickedSquares(new Array(denominator).fill(false))
    onAnswerStateChange(isCorrect)
  }

  const onSquareClick = (i: number): void => {
    setClickedSquares((prev) => {
      const newState = [...prev]
      newState[i] = !newState[i]
      return newState
    })
  }

  const resetAnswer = (): void => {
    setClickedSquares(new Array(denominator).fill(false))
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prev) => [{ numerator, denominator }, ...prev].slice(0, MAX_HISTORY_LENGTH[difficultyLevel - 1]))
    onAnswerStateChange(null)
    resetAnswer()
    onNextQuestionClick()
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper className="flex-col">
        <div className="w-6 flex flex-col items-center">
          {numerator}
          <Divider />
          {denominator}
        </div>
        <div className="relative flex flex-wrap">
          {Array.from({ length: denominator }).map((_, i) => (
            <div
              className={`border border-collapse border-black w-12 h-12 ${
                clickedSquares[i] ? 'bg-pink' : 'bg-gray-medium'
              } inline-block`}
              key={i.toString() + denominator}
              onClick={() => onSquareClick(i)}
            />
          ))}
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => resetAnswer(),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FractionVisualize
